import htmx from "htmx.org";
import type { PlaceAutocomplete } from "./blocks/map";
import type { LocationPoint } from "./blocks/geolocation";

const formatInventoryCoordinates = (coords: LocationPoint | null) =>
  coords ? `${coords.lat.toFixed(6)},${coords.lng.toFixed(6)}` : "";

const INVENTORY_COORDINATES_COOKIE_NAME = "inventory_coordinates";
const INVENTORY_COORDINATES_COOKIE_MAX_AGE_SECONDS = 330;

const getInventoryCoordinatesCookieAttributes = (maxAge: number) =>
  `Max-Age=${maxAge}; Path=/; SameSite=Lax`;

const setInventoryCoordinatesCookie = (coords: LocationPoint | null) => {
  if (!coords) {
    return;
  }
  const value = encodeURIComponent(formatInventoryCoordinates(coords));
  document.cookie = `${INVENTORY_COORDINATES_COOKIE_NAME}=${value}; ${getInventoryCoordinatesCookieAttributes(INVENTORY_COORDINATES_COOKIE_MAX_AGE_SECONDS)}`;
};

const hasInventoryCoordinatesCookie = () =>
  document.cookie
    .split(";")
    .some((cookie) => cookie.trim().startsWith(`${INVENTORY_COORDINATES_COOKIE_NAME}=`));

const getCoordinatesInput = () =>
  document.getElementById("filter-coordinates") as HTMLInputElement | null;

const getInventoryCoordinatesInput = (element: HTMLElement) => {
  const inputId = element.dataset.coordinatesInputId;
  if (!inputId) {
    return null;
  }
  return document.getElementById(inputId);
};

const refreshInventoryFilters = () => {
  const form = document.querySelector<HTMLFormElement>(".inventory-filter-list");
  if (form) {
    htmx.trigger(form, "change");
  }
};

const setInventoryCoordinatesFilter = (
  coordinatesInput: HTMLElement,
  coords: LocationPoint | null,
) => {
  if (coordinatesInput instanceof HTMLInputElement) {
    coordinatesInput.value = formatInventoryCoordinates(coords);
  }
  refreshInventoryFilters();
};

const setPlaceAutocompleteLoading = (input: HTMLElement, isLoading: boolean) => {
  input.classList.toggle("placeholder", isLoading);
  input.classList.toggle("placeholder-wave", isLoading);
};

const bindInventoryPlaceAutocompleteRequestState = (autocomplete: PlaceAutocomplete) => {
  const form = document.querySelector<HTMLFormElement>(".inventory-filter-list");
  if (!form) {
    return;
  }
  form.addEventListener("htmx:beforeRequest", () => {
    autocomplete.disabled = true;
  });
  form.addEventListener("htmx:afterRequest", () => {
    autocomplete.disabled = false;
  });
};

const initInventoryPlaceAutocomplete = (input: HTMLElement) =>
  import(/* webpackChunkName: "blocks", webpackPrefetch: true */ "./blocks/map")
    .then(({ initPlaceAutocomplete }) => {
      setPlaceAutocompleteLoading(input, true);
      const coordinatesInput = getInventoryCoordinatesInput(input);
      if (!coordinatesInput || !input.isConnected) {
        setPlaceAutocompleteLoading(input, false);
        return null;
      }
      const hasInitialCoordinates =
        coordinatesInput instanceof HTMLInputElement && Boolean(coordinatesInput.value);

      return initPlaceAutocomplete(input)
        .then((autocomplete) => {
          autocomplete.classList.remove("placeholder", "placeholder-wave");
          bindInventoryPlaceAutocompleteRequestState(autocomplete);
          if (input.dataset.nearestCoordinates) {
            autocomplete.setPlaceholder("Device location...");
          }
          if (hasInitialCoordinates) {
            autocomplete.value = "Custom Location";
          }
          autocomplete.addSelectListener((coords) => {
            const currentCoordinatesInput = getInventoryCoordinatesInput(input);
            if (currentCoordinatesInput) {
              setInventoryCoordinatesFilter(currentCoordinatesInput, coords);
            }
          });
          return autocomplete;
        })
        .catch(() => {
          setPlaceAutocompleteLoading(input, false);
          return null;
        });
    })
    .catch(() => null);

const initInventoryAutodetectedCoordinates = () => {
  const coordinatesInput = getCoordinatesInput();
  if (
    !coordinatesInput ||
    coordinatesInput.value ||
    hasInventoryCoordinatesCookie() ||
    !navigator.geolocation
  ) {
    return Promise.resolve(null);
  }

  return import(
    /* webpackChunkName: "blocks", webpackPrefetch: true */ "./blocks/geolocation"
  )
    .then(({ requestGeolocation }) => requestGeolocation())
    .then((coords) => {
      if (coords) {
        setInventoryCoordinatesCookie(coords);
        refreshInventoryFilters();
      }
      return coords;
    })
    .catch(() => null);
};

export const initInventory = () => {
  const placeAutocompleteInput = document.querySelector<HTMLElement>(
    "[data-inventory-place-autocomplete]",
  );
  Promise.all([
    placeAutocompleteInput
      ? initInventoryPlaceAutocomplete(placeAutocompleteInput)
      : Promise.resolve(null),
    initInventoryAutodetectedCoordinates(),
  ]).then(([autocomplete, coords]) => {
    if (!autocomplete) {
      return;
    }
    if (coords) {
      autocomplete.setPlaceholder("Device location...");
    }
  });
};
